import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  Fab,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  Event as EventIcon,
  EmojiEvents as DistributionIcon,
  Gavel as GavelIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  Speed as SpeedIcon,
  Notifications as NotificationsIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as LoanIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useFeatures } from '../hooks/useFeatures';
import { useMandalName, useDistribution } from '../hooks/useMandalName';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { DOMAIN, tint } from '../theme/accents';
import NotificationCenter from './NotificationCenter';
import QuickCollection from './QuickCollection';

const drawerWidth = 240;

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const theme = useTheme();
  // Below md the sidebar overlays the page instead of taking a fixed slice of it -
  // a 240px permanent drawer leaves almost nothing on a phone.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Sections the group has switched off are hidden from the sidebar
  const { features } = useFeatures();
  const { mode, toggleMode } = useThemeMode();
  const mandalName = useMandalName();
  const { title: distributionTitle } = useDistribution();
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [quickCollectionOpen, setQuickCollectionOpen] = useState(false);

  // Start closed on small screens, and close again whenever we cross the breakpoint
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  // Tapping a destination on mobile should dismiss the overlay
  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setOpen(false);
    }
  };

  // Width the content must leave for the drawer
  const railWidth = isMobile ? 0 : (open ? drawerWidth : 60);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Each destination carries its domain colour, so the sidebar reads as a map
  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', accent: DOMAIN.dashboard },
    { text: 'Payments', icon: <PaymentIcon />, path: '/payments', accent: DOMAIN.payments },
    { text: 'Penalties', icon: <WarningIcon />, path: '/penalties', accent: DOMAIN.penalties, feature: 'featurePenalties' },
    { text: 'Expenses', icon: <ReceiptIcon />, path: '/expenses', accent: DOMAIN.expenses, feature: 'featureExpenses' },
    { text: 'Loans', icon: <LoanIcon />, path: '/loans', accent: DOMAIN.loans, feature: 'featureLoans' },
    { text: 'Members', icon: <PeopleIcon />, path: '/members', accent: DOMAIN.members },
    { text: 'Meetings', icon: <EventIcon />, path: '/meetings', accent: DOMAIN.meetings, feature: 'featureMeetings' },
    { text: distributionTitle, icon: <DistributionIcon />, path: '/distribution', accent: DOMAIN.distribution, feature: 'featureSilverDistribution' },
    { text: 'Rules', icon: <GavelIcon />, path: '/rules', accent: DOMAIN.rules },
  ];

  const adminItems = [
    { text: 'Admin Panel', icon: <AdminIcon />, path: '/admin', accent: DOMAIN.admin },
    { text: 'Audit Logs', icon: <SecurityIcon />, path: '/audit', accent: DOMAIN.audit },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        // Without this the bar keeps primary.contrastText (white), which is
        // invisible against the paper ground in light mode
        color="inherit"
        enableColorOnDark
        sx={{
          width: `calc(100% - ${railWidth}px)`,
          ml: `${railWidth}px`,
          transition: 'width 0.3s, margin 0.3s',
          // Quiet chrome - the gold is spent on the numbers, not the frame
          backgroundColor: 'background.default',
          backgroundImage: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          color: 'text.primary',
        }}
      >
        {/* xs padding matches the hamburger's -12px edge margin so it sits inside the screen */}
        <Toolbar sx={{ px: { xs: 1.5, sm: 2, md: 3 } }}>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: { xs: 0.5, sm: 2 } }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, minWidth: 0, fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {mandalName}
          </Typography>
          {isAdmin && (
            <Chip
              label="ADMIN"
              color="warning"
              size="small"
              sx={{ mr: 2, display: { xs: 'none', sm: 'inline-flex' } }}
            />
          )}
          <Tooltip title={mode === 'light' ? 'Switch to dark' : 'Switch to light'}>
            <IconButton color="inherit" onClick={toggleMode} aria-label="toggle colour mode" sx={{ mr: 0.5 }}>
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>
          <NotificationCenter />
          <IconButton
            size={isMobile ? 'small' : 'large'}
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ bgcolor: 'secondary.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
              {user?.fullName?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}>
              <ListItemIcon><AccountIcon fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { navigate('/notification-settings'); handleProfileMenuClose(); }}>
              <ListItemIcon><NotificationsIcon fontSize="small" /></ListItemIcon>
              Notification Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: isMobile ? 0 : (open ? drawerWidth : 60),
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isMobile ? drawerWidth : (open ? drawerWidth : 60),
            boxSizing: 'border-box',
            transition: 'width 0.3s',
            backgroundColor: mode === 'light' ? '#F4EDE2' : '#1A1613',
            backgroundImage: 'none',
            borderRight: '1px solid',
            borderColor: 'divider',
            overflowX: 'hidden',
          },
        }}
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? open : true}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        anchor="left"
      >
        <Toolbar>
          {open && (
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: '-0.01em',
              }}
            >
              {mandalName}
            </Typography>
          )}
        </Toolbar>
        <Divider />
        <List>
          {menuItems.filter(item => !item.feature || features[item.feature]).map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: tint(item.accent, 0.14),
                    borderLeft: '3px solid',
                    borderColor: item.accent,
                    '&:hover': { backgroundColor: tint(item.accent, 0.22) },
                  },
                  '&:hover': { backgroundColor: tint(item.accent, 0.08) },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: item.accent,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && <ListItemText primary={item.text} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {isAdmin && (
          <>
            <Divider />
            <List>
              {adminItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      '&.Mui-selected': {
                        backgroundColor: tint(item.accent, 0.14),
                        borderLeft: '3px solid',
                        borderColor: item.accent,
                      },
                      '&:hover': { backgroundColor: tint(item.accent, 0.08) },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                        color: item.accent,
                      }}
                    >
                      <Badge badgeContent="Admin" color="warning" invisible={!open}>
                        {item.icon}
                      </Badge>
                    </ListItemIcon>
                    {open && <ListItemText primary={item.text} />}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: { xs: 2, md: 3 },
          // Room under the last row for the floating collection button
          pb: { xs: 11, md: 3 },
          width: `calc(100% - ${railWidth}px)`,
          maxWidth: '100%',
          overflowX: 'hidden',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {/* Cap the reading width - stretched across an ultrawide monitor the
            content thins out and every card becomes mostly empty space */}
        <Box sx={{ maxWidth: 1600, mx: 'auto', width: '100%' }}>
          <Outlet />
        </Box>
      </Box>
      
      {/* Quick Collection FAB - Only for admins */}
      {isAdmin && (
        <>
          <Tooltip title="Quick Collection" placement="left">
            <Fab
              color="secondary"
              aria-label="quick collection"
              onClick={() => setQuickCollectionOpen(true)}
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
              }}
            >
              <SpeedIcon />
            </Fab>
          </Tooltip>
          <QuickCollection 
            open={quickCollectionOpen} 
            onClose={() => setQuickCollectionOpen(false)}
            onSuccess={() => {
              // Dispatch a custom event that the payments page can listen to
              window.dispatchEvent(new CustomEvent('paymentCreated', { 
                detail: { source: 'quickCollection' }
              }));
            }}
          />
        </>
      )}
    </Box>
  );
};

export default Layout;